import React, { useState } from 'react';
import { useParams } from 'react-router';
import {
    Box, Stack, Typography, Collapse, IconButton, Chip, Divider,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import LoadingIndicator from '../LoadingIndicator';
import CategoryItemTable from './CategoryItemTable';
import { useGetProcedureItemInsightsQuery } from '../../api/procedureSlice';
import { formatCurrency, formatPercentage } from '../../utils/formatNumbers';

function CategoryRow({ category }) {
    const [open, setOpen] = useState(false);

    return (
        <Box
            sx={{
                borderRadius: 2,
                boxShadow: 1,
                mb: 1.5,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            {/* ── Header ── */}
            <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                onClick={() => setOpen((v) => !v)}
                sx={{
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    backgroundColor: open ? 'background.selectedRow' : 'background.card',
                    '&:hover': { backgroundColor: 'action.hover' },
                }}
            >
                <IconButton size="small" disableRipple>
                    {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </IconButton>

                <Typography variant="heavy" sx={{ flex: 1 }}>
                    {category.category}
                </Typography>

                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent='space-between'>
                    <StatPill label="Avg Spend" value={formatCurrency(category.category_average_spend)} />
                    {/* <StatPill label="Coeff. Variance" value={formatPercentage(category.category_coefficient_variance)} color="warning.main" /> */}
                    <Chip label={`${category.items?.length ?? 0} items`} size="small" variant="outlined" />
                </Stack>
            </Stack>

            {/* ── Collapsible body ── */}
            <Collapse in={open}>
                <Divider />
                <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
                    <CategoryItemTable items={category.items} procedureId={useParams().procedureId} />
                </Box>
            </Collapse>
        </Box>
    );
}

function StatPill({ label, value, color = 'text.primary' }) {
    return (
        <Stack alignItems="center" sx={{ minWidth: 70 }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="heavy" fontSize={13} color={color}>{value}</Typography>
        </Stack>
    );
}

function CategoryBreakdown() {
    const procedureId = useParams().procedureId;
    const { data, isLoading, isSuccess } = useGetProcedureItemInsightsQuery(procedureId);

    const sorted = React.useMemo(() => {
        if (!data) return [];
        return [...data].sort((a, b) => b.category_average_spend - a.category_average_spend);
    }, [data]);

    return (
        <Box px={3} pb={4}>
            <Typography variant="bold" fontSize="20px" mb={2} display="block">
                Category Breakdown
            </Typography>

            {isLoading && <LoadingIndicator/>}
            {isSuccess && sorted.map((category) => (
                <CategoryRow key={category.category} category={category} />
            ))}
        </Box>
    );
}

export default CategoryBreakdown;
